import React, { useEffect, useId } from "react";
import { useTranslation } from "react-i18next";
import { ArrowsRightLeftIcon, CalendarDaysIcon } from "@heroicons/react/24/outline";
import { DifficultyBadge } from "../../../components/Difficulty/Difficulty";
import { isBlankHtml, RichText } from "../../../components/KaTeXComponent/RichText";
import { cardClass, linkButtonClass } from "../../Exams/TestBuilder/ui";
import {
  AnswerOption,
  ContentSkeleton,
  LatestTag,
  LoadError,
  VersionByline,
  VersionStatus,
  versionLabel,
} from "./parts";

// A version's content as the question page shows a question: its attributes, then its text and options.
function VersionContent({ data }) {
  const { t } = useTranslation();
  const labels = data.labels || {};
  const attributes = [
    ["topic", labels.topic],
    ["subtopic", labels.subtopic],
    ["objective", labels.objective],
  ].filter(([, value]) => value);
  const answers = data.answer_keys || [];

  return (
    <div className="space-y-5">
      {(data.difficulty || labels.purpose) && (
        <div className="flex flex-wrap items-center gap-2">
          <DifficultyBadge level={data.difficulty} withLabel />
          {labels.purpose && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-500/20">
              <CalendarDaysIcon className="h-3.5 w-3.5 text-slate-500" aria-hidden="true" />
              <span className="sr-only">{t("question_versions.fields.purpose")}: </span>
              {labels.purpose}
            </span>
          )}
        </div>
      )}
      {attributes.length > 0 && (
        <dl className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {attributes.map(([field, value]) => (
            <div key={field} className="flex min-w-0 items-baseline gap-2">
              <dt className="shrink-0 text-xs font-medium text-slate-500">{t(`question_versions.fields.${field}`)}</dt>
              <dd className="min-w-0 break-words text-slate-700">{value}</dd>
            </div>
          ))}
        </dl>
      )}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
        <div className="mb-4 break-words text-base font-medium leading-relaxed text-slate-900">
          {isBlankHtml(data.body) ? (
            <p className="text-sm font-normal italic text-slate-500">{t("question_versions.empty_body")}</p>
          ) : (
            <RichText html={data.body} />
          )}
        </div>
        {answers.length ? (
          <div className="space-y-2">
            {answers.map((answer, index) => (
              <AnswerOption key={`${answer.option}-${index}`} answer={answer} />
            ))}
          </div>
        ) : (
          <p className="text-sm italic text-slate-500">{t("question_versions.no_options")}</p>
        )}
      </div>
    </div>
  );
}

// A quiet link (the version's own actions are secondary), 44px tall on phones.
const compareLink = `${linkButtonClass} max-md:min-h-[2.75rem]`;

/**
 * One version: `row` its line of the list, `state` its content ({loading} | {data} | {error}), read through
 * `request`. `previous` and `latest` (numbers or null) offer to compare it with them, but for the pair `listPair`
 * (two numbers, or null) that the list beside it already offers.
 */
export default function VersionView({ row, state, request, previous, latest, listPair = null, onCompare, headingRef }) {
  const { t } = useTranslation();
  const titleId = useId();
  const number = row.number;

  useEffect(() => {
    request(number);
  }, [request, number]);

  const notFound = state.error?.response?.status === 404;
  const offered = (a, b) => !listPair || Math.min(a, b) !== Math.min(...listPair) || Math.max(a, b) !== Math.max(...listPair);
  const withPrevious = previous != null && offered(previous, number);
  const withLatest = latest != null && latest !== number && offered(number, latest);

  return (
    <article aria-labelledby={titleId} className={`${cardClass} min-w-0`}>
      <header className="space-y-3 border-b border-slate-100 p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3
              id={titleId}
              ref={headingRef}
              tabIndex={-1}
              className="flex flex-wrap items-center gap-2 text-xl font-semibold tracking-tight text-slate-900 focus:outline-none"
            >
              {versionLabel(t, number)}
              {number === latest && <LatestTag />}
            </h3>
            <VersionByline version={row} className="mt-1.5" />
          </div>
          <VersionStatus code={row.status_code} />
        </div>
        {row.reason === "baseline" && <p className="text-xs text-slate-500">{t("question_versions.baseline_hint")}</p>}
        {(withPrevious || withLatest) && (
          <div className="-ml-1.5 flex flex-wrap gap-x-3 gap-y-1">
            {withPrevious && (
              <button type="button" onClick={() => onCompare(previous, number)} className={compareLink}>
                <ArrowsRightLeftIcon className="h-4 w-4" aria-hidden="true" />
                {t("question_versions.compare_previous")}
              </button>
            )}
            {withLatest && (
              <button type="button" onClick={() => onCompare(number, latest)} className={compareLink}>
                <ArrowsRightLeftIcon className="h-4 w-4" aria-hidden="true" />
                {t("question_versions.compare_latest")}
              </button>
            )}
          </div>
        )}
      </header>
      <div className="p-5 sm:p-6">
        {state.data ? (
          <VersionContent data={state.data.data || {}} />
        ) : state.error ? (
          <LoadError
            message={t(notFound ? "question_versions.version_not_found" : "question_versions.version_failed", { number })}
            onRetry={notFound ? undefined : () => request(number)}
          />
        ) : (
          <div role="status">
            <span className="sr-only">{t("question_detail.loading")}</span>
            <ContentSkeleton />
          </div>
        )}
      </div>
    </article>
  );
}
